import { Alert, Box, Button, Skeleton, Stack, Tooltip, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { ArtifactDetailHeader, Button as WaitButton } from '@verantos-dev/shared-components';

import { AnalysisDefinitionTab } from '../containers/definition/AnalysisDefinitionTab';
import { useAnalysisBreadcrumbs } from '../hooks/useAnalysisBreadcrumbs';
import { useAnalysisDetailHeaderMeta } from '../hooks/useAnalysisDetailHeaderMeta';
import { useAnalysisDetailPage } from '../hooks/useAnalysisDetailPage';
import { useAnalysisProcess } from '../hooks/useAnalysisProcess';
import { useAnalysisSourceCohort } from '../hooks/useAnalysisSourceCohort';
import { useHydrateAnalysisDefinition } from '../hooks/useHydrateAnalysisDefinition';
import { useProcessingOverdue } from '../hooks/useProcessingOverdue';
import { useRefreshOnSourceCohortChange } from '../hooks/useRefreshOnSourceCohortChange';

export const AnalysisDetailPage = () => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const { pageTitle, isProfileLoading, latestStatus, isMetaReady, ownerName, lastModifiedDate } =
    useAnalysisDetailPage();
  useHydrateAnalysisDefinition();
  useRefreshOnSourceCohortChange();
  const { onProcess, isSubmitting, canProcess, processTooltip, processBlockers, processError } =
    useAnalysisProcess();
  const breadcrumbs = useAnalysisBreadcrumbs(pageTitle);
  const { isOverdue: isProcessingOverdue, onCheckAgain } = useProcessingOverdue();
  // The dataset the analysis runs on is its cohort's. Its own header item, not part of
  // the cohort's row, since it describes the analysis as a whole.
  const { selectedCohort } = useAnalysisSourceCohort();
  const { effectiveStatus, metaItems } = useAnalysisDetailHeaderMeta({
    latestStatus,
    isMetaReady,
    ownerName,
    lastModifiedDate,
    datasetName: selectedCohort?.latestDatasetReleaseDisplayName || null,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
      {isProfileLoading ? (
        // Matches the header's vertical rhythm so the swap doesn't shift below.
        <Box sx={{ px: 6 }}>
          <Stack spacing={1} sx={{ py: 2 }} data-testid="analysis-detail-header-loading">
            <Skeleton variant="text" width={220} height={20} />
            <Skeleton variant="text" width={320} height={40} />
          </Stack>
        </Box>
      ) : (
        <ArtifactDetailHeader
          id="analysisDetailPageHeader"
          breadcrumbs={breadcrumbs}
          title={pageTitle}
          status={effectiveStatus}
          titleTestId="analysis-detail-title"
          statusTestId="analysis-detail-status"
          metaItems={metaItems}
          // The shared header's opt-in condensed layout (two-line title, capped crumbs,
          // aligned meta icons). This page is reachable only behind descriptive_analysis_impl.
          condensed
          // TODO: add the Download button once the backend exposes an analysis export endpoint.
          rightContent={
            <Tooltip
              title={
                processBlockers.length > 0 ? (
                  <Box data-testid="analysis-detail-process-blockers">
                    {formatMessage({ id: 'analysisDetail.processTooltip.blockersTitle' })}
                    <Box component="ul" sx={{ m: 0, pl: 4 }}>
                      {processBlockers.map((blocker) => (
                        <li key={blocker}>{blocker}</li>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  processTooltip ?? ''
                )
              }
              arrow
            >
              <span>
                <WaitButton
                  id="analysis-detail-process"
                  btnType="primary"
                  size="small"
                  waitButton={isSubmitting}
                  displayLabel={formatMessage({ id: 'analysisDetail.processButton' })}
                  disabled={!canProcess}
                  onClickHandler={onProcess}
                  sx={{ textTransform: 'none' }}
                />
              </span>
            </Tooltip>
          }
        />
      )}
      {isProcessingOverdue && (
        <Box sx={{ px: 6 }}>
          <Alert
            severity="warning"
            data-testid="analysis-detail-processing-overdue"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={onCheckAgain}
                data-testid="analysis-detail-processing-check-again"
              >
                {formatMessage({ id: 'analysisDetail.processingOverdue.checkAgain' })}
              </Button>
            }
          >
            {formatMessage({ id: 'analysisDetail.processingOverdue.message' })}
          </Alert>
        </Box>
      )}
      {processError && (
        <Box sx={{ px: 6 }}>
          <Alert severity="error" data-testid="analysis-detail-process-error">
            {formatMessage({ id: 'analysisDetail.processError' })}
          </Alert>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          px: 6,
          py: 3,
          mt: theme.spacing(1),
        }}
      >
        <AnalysisDefinitionTab />
      </Box>
    </Box>
  );
};
